import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Tabs,
  Tab,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material'; // Correction du chemin d'import
import { DateTimePicker } from '@mui/x-date-pickers'; // Correction du chemin d'import
import { differenceInHours, differenceInMinutes } from 'date-fns';
import { supabase } from '../../supabaseClient';
import { FULL_DAY_HOURS } from '../../config/timeConstants';
import AttachmentList from './AttachmentList';
import PointageSelector from './PointageSelector';
import NotesList from './NotesList';
import { useSecteur } from '../../contexts/SecteurContext';
import { useAuth } from '../../contexts/AuthContext';
import { Delete as DeleteIcon, Assignment as AssignmentIcon, Group as GroupIcon, AttachFile as AttachFileIcon, Note as NoteIcon } from '@mui/icons-material';

export default function EventDialog({ open, event, onClose, onSave }) {
  const { selectedSecteur } = useSecteur();
  const { roles } = useAuth();
  const canEdit = roles?.can_manage_sector_calendar;
  const [currentTab, setCurrentTab] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    lieu: '', // Ajout du champ lieu
    start_date: new Date(),
    end_date: new Date(),
    all_day: false,
    color: '#3788d8'
  });

  const [newPointage, setNewPointage] = useState({
    userId: '',
    releveCodeId: '',
    hours: FULL_DAY_HOURS
  });

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.down('md'));

  // Envelopper calculateEventDuration dans un useCallback
  const calculateEventDuration = useCallback((start, end) => {
    if (!start || !end) return FULL_DAY_HOURS;
    
    if (formData.all_day) return FULL_DAY_HOURS;

    const startDate = new Date(start);
    const endDateTime = new Date(end);
    
    const hours = differenceInHours(endDateTime, startDate);
    const remainingMinutes = differenceInMinutes(endDateTime, startDate) % 60;
    
    return hours + (remainingMinutes / 60);
  }, [formData.all_day]); // Ajouter la dépendance formData.all_day

  useEffect(() => {
    if (event) {
    

      const startDate = event.start ? new Date(event.start) : new Date();
      const endDate = event.end ? new Date(event.end) : new Date();

      // Correction robuste pour le champ lieu
      let lieu = '';
      if ('lieu' in event && event.lieu) {
        lieu = event.lieu;
      } else if (event.extendedProps && event.extendedProps.lieu) {
        lieu = event.extendedProps.lieu;
      } else if (event._def && event._def.extendedProps && event._def.extendedProps.lieu) {
        // Cas FullCalendar v6+
        lieu = event._def.extendedProps.lieu;
      }

      setFormData({
        title: event.title || '',
        description: event.description || '',
        lieu, // Correction ici
        start_date: startDate,
        end_date: endDate,
        all_day: event.allDay || false,
        color: event.color || '#3788d8'
      });

      setNewPointage(prev => ({
        ...prev,
        hours: event.allDay ? FULL_DAY_HOURS : calculateEventDuration(startDate, endDate)
      }));
    }
  }, [event, calculateEventDuration]); // Ajouter calculateEventDuration aux dépendances

  const handleSave = async () => {
    try {
      // Correction : retirez la propriété all_day de l'objet eventData
      const { all_day, ...eventData } = {
        ...formData,
        sector_id: selectedSecteur,
        start_date: formData.start_date.toISOString(),
        end_date: formData.end_date.toISOString(),
        lieu: formData.lieu // S'assurer que le champ lieu est bien envoyé
      };

      let eventId = event?.id;
      let result;

      // Sauvegarder ou mettre à jour l'événement
      if (eventId) {
        result = await supabase
          .from('sector_events')
          .update(eventData)
          .eq('id', eventId);
      } else {
        result = await supabase
          .from('sector_events')
          .insert([eventData])
          .select()
          .single();
        
        if (result.data) {
          eventId = result.data.id;
        }
      }

      if (result.error) throw result.error;

      // Récupérer tous les pointages associés à l'événement
      const { data: eventPointages, error: pointagesError } = await supabase
        .from('event_pointages')
        .select('pointage_id')
        .eq('event_id', eventId);

      if (pointagesError) throw pointagesError;

      if (eventPointages?.length > 0) {
        // Créer la description pour les pointages
        const pointageDescription = formData.title && formData.description
          ? `${formData.title}: ${formData.description}`
          : formData.title || formData.description || '';

        // Mettre à jour la description de tous les pointages associés
        const { error: updateError } = await supabase
          .from('pointage')
          .update({ description: pointageDescription })
          .in('id', eventPointages.map(ep => ep.pointage_id));

        if (updateError) throw updateError;
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const handleDelete = async () => {
    try {
      // 1. Récupérer d'abord les pièces jointes pour avoir les chemins des fichiers
      const { data: attachments, error: attachmentsError } = await supabase
        .from('event_attachments')
        .select('file_path')
        .eq('event_id', event.id);

      if (attachmentsError) throw attachmentsError;

      // 2. Supprimer les fichiers du bucket s'il y en a
      if (attachments?.length > 0) {
        // Tous les fichiers sont dans un dossier nommé avec l'id de l'événement
        // Donc on supprime le dossier entier
        const { error: storageError } = await supabase.storage
          .from('event-attachments')
          .remove([`${event.id}/`]);

        if (storageError && storageError.statusCode !== '404') {
          // Ignorer l'erreur 404 (pas trouvé) car le dossier peut ne pas exister
          throw storageError;
        }
      }

      // 3. Supprimer les autres données dans l'ordre pour respecter les contraintes de clés étrangères
      const promises = [
        // Supprimer les associations event_pointages
        supabase
          .from('event_pointages')
          .delete()
          .eq('event_id', event.id),
        
        // Supprimer les pièces jointes de la base
        supabase
          .from('event_attachments')
          .delete()
          .eq('event_id', event.id),

        // Supprimer les notes
        supabase
          .from('event_notes')
          .delete()
          .eq('event_id', event.id)
      ];

      await Promise.all(promises);

      // 4. Supprimer l'événement en dernier
      const { error: deleteError } = await supabase
        .from('sector_events')
        .delete()
        .eq('id', event.id);

      if (deleteError) throw deleteError;

      onSave(); // Rafraîchir la liste des événements
      onClose();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullScreen={isXs}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: isXs ? '100vw' : isSm ? '95vw' : '80vw',
          height: isXs ? '100vh' : isSm ? '95vh' : '80vh',
          maxHeight: isXs ? '100vh' : isSm ? '95vh' : '80vh',
          m: 0,
          borderRadius: isXs ? 0 : 2,
          p: isXs ? 0 : 2,
          // Pour éviter le débordement sur mobile
          overflow: 'auto'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: isXs ? 2 : 3, py: isXs ? 1 : 2 }}>
        {event?.id ? 'Modifier l\'événement' : 'Nouvel événement'}
        {event?.id && canEdit && (
          <IconButton 
            color="error" 
            onClick={handleDelete}
            sx={{ ml: 2 }}
            disabled={!canEdit}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent sx={{ px: isXs ? 1 : 3, py: isXs ? 1 : 2 }}>
        <Box sx={{ width: '100%' }}>
          <Tabs
            value={currentTab}
            onChange={(e, v) => setCurrentTab(v)}
            variant={isXs ? 'scrollable' : 'standard'}
            scrollButtons={isXs ? 'auto' : false}
            allowScrollButtonsMobile
            centered={!isXs}
          >
            <Tab icon={<AssignmentIcon />} aria-label="Détails" />
            <Tab icon={<GroupIcon />} aria-label="Participants" disabled={!event?.id} />
            <Tab icon={<AttachFileIcon />} aria-label="Pièces jointes" disabled={!event?.id} />
            <Tab icon={<NoteIcon />} aria-label="Notes" disabled={!event?.id} />
          </Tabs>

          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              maxHeight: isXs ? 'calc(100vh - 180px)' : 'auto',
              overflowY: isXs ? 'auto' : 'visible'
            }}
          >
            {currentTab === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: isXs ? 'column' : 'column',
                  gap: 2
                }}
              >
                <TextField
                  fullWidth
                  label="Titre"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                  disabled={!canEdit}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={isXs ? 2 : 4}
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                  disabled={!canEdit}
                />
                <TextField
                  fullWidth
                  label="Lieu"
                  value={formData.lieu}
                  onChange={(e) => setFormData(prev => ({...prev, lieu: e.target.value}))}
                  disabled={!canEdit}
                  sx={{ mb: 2 }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: isXs ? 'column' : 'row',
                    gap: 2
                  }}
                >
                  <DateTimePicker
                    label="Début"
                    value={formData.start_date}
                    onChange={(date) => setFormData(prev => ({
                      ...prev, 
                      start_date: date || new Date()
                    }))}
                    disabled={!canEdit}
                    slotProps={{
                      textField: { fullWidth: true }
                    }}
                  />
                  <DateTimePicker
                    label="Fin"
                    value={formData.end_date}
                    onChange={(date) => setFormData(prev => ({
                      ...prev, 
                      end_date: date || new Date()
                    }))}
                    disabled={!canEdit}
                    slotProps={{
                      textField: { fullWidth: true }
                    }}
                  />
                </Box>
              </Box>
            )}

            {currentTab === 1 && (
              !event?.id ? (
                <Box sx={{ p: 2, color: 'text.secondary' }}>
                  Veuillez enregistrer l'événement avant d'ajouter des participants.
                </Box>
              ) : (
                <Box sx={{ width: '100%', minHeight: isXs ? 200 : 'auto' }}>
                  <PointageSelector 
                    eventId={event?.id}
                    date={formData.start_date}
                    endDate={formData.end_date}
                    allDay={formData.all_day}
                    onPointageChange={setNewPointage}
                    newPointage={newPointage}
                    canEdit={canEdit}
                    sx={{
                      display: 'flex',
                      flexDirection: isXs ? 'column' : 'row',
                      flexWrap: 'wrap',
                      alignItems: 'stretch'
                    }}
                  />
                </Box>
              )
            )}

            {currentTab === 2 && (
              !event?.id ? (
                <Box sx={{ p: 2, color: 'text.secondary' }}>
                  Veuillez enregistrer l'événement avant d'ajouter des pièces jointes.
                </Box>
              ) : (
                <Box sx={{ width: '100%', minHeight: isXs ? 200 : 'auto' }}>
                  <AttachmentList eventId={event?.id} canEdit={canEdit} />
                </Box>
              )
            )}
            {currentTab === 3 && (
              !event?.id ? (
                <Box sx={{ p: 2, color: 'text.secondary' }}>
                  Veuillez enregistrer l'événement avant d'ajouter des notes.
                </Box>
              ) : (
                <Box sx={{ width: '100%', minHeight: isXs ? 200 : 'auto' }}>
                  <NotesList eventId={event?.id} canEdit={canEdit} />
                </Box>
              )
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: isXs ? 1 : 3, py: isXs ? 1 : 2 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={handleSave} color="primary" disabled={!canEdit}>
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
